'use client';

import { c as _c } from "react/compiler-runtime";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { httpBatchLink, loggerLink, TRPCClientError, type TRPCLink } from '@trpc/client';
import { observable } from '@trpc/server/observable';
import React, { useState } from 'react';
import superjson from 'superjson';
import { localeFromPathname, localizePath, stripLocalePrefix } from '~/i18n/i18n-config';
import type { AppRouter } from '~/server/api/root';
import { api } from '~/trpc/react-client';
import { logError } from '~/utils';
import { isClientConsoleLoggingEnabled } from '~/utils/logger';
import { getMiddlewareUrl } from '~/utils/server';

// Mirrors proxy.ts's isGatedRoute: an expired session on one of these has to recover, and
// anywhere else it must not — a public page hitting an authenticated query is expected to fail.
const GATED_PREFIXES = ['/account', '/_internal'];
const SIGN_IN_PATH = '/auth/sign-in';
const authRedirectLink: TRPCLink<AppRouter> = () => ({
  op,
  next
}) => observable(observer => {
  const subscription = next(op).subscribe({
    next(result) {
      observer.next(result);
    },
    error(err) {
      if (err instanceof TRPCClientError) {
        const code = err.shape?.data?.code ?? err.data?.code;
        if (code === 'UNAUTHORIZED' && globalThis.location !== undefined) {
          const {
            pathname,
            href
          } = globalThis.location;
          // `localePrefix: 'as-needed'` means the same route arrives as either
          // /account/... or /en-US/account/..., so matching the raw pathname silently
          // skips recovery for every non-default locale.
          const routePath = stripLocalePrefix(pathname);
          if (GATED_PREFIXES.some(prefix => routePath.startsWith(prefix))) {
            const signInPath = localizePath(SIGN_IN_PATH, localeFromPathname(pathname));
            globalThis.location.assign(`${signInPath}?redirect_url=${encodeURIComponent(href)}`);
          }
        }
      }
      observer.error(err);
    },
    complete() {
      observer.complete();
    }
  });
  return () => {
    subscription.unsubscribe();
  };
});
export const TRPCReactProvider = t0 => {
  const $ = _c(10);
  const {
    children
  } = t0;
  const [queryClient] = useState(_temp);
  let t1;
  let t2;
  if ($[0] !== queryClient) {
    t1 = () => {
      const unsubscribeQuery = queryClient.getQueryCache().subscribe(_temp2);
      const unsubscribeMutation = queryClient.getMutationCache().subscribe(_temp3);
      return () => {
        unsubscribeQuery();
        unsubscribeMutation();
      };
    };
    t2 = [queryClient];
    $[0] = queryClient;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  React.useEffect(t1, t2);
  const [trpcClient] = useState(_temp5);
  let t3;
  if ($[3] !== children || $[4] !== queryClient || $[5] !== trpcClient) {
    t3 = <api.Provider client={trpcClient} queryClient={queryClient}>{children}</api.Provider>;
    $[3] = children;
    $[4] = queryClient;
    $[5] = trpcClient;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== queryClient || $[8] !== t3) {
    t4 = <QueryClientProvider client={queryClient}>{t3}</QueryClientProvider>;
    $[7] = queryClient;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};
function _temp() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 60000,
        cacheTime: 300000,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchOnMount: true,
        retry: 1,
        networkMode: "online"
      },
      mutations: {
        retry: 0,
        networkMode: "online"
      }
    }
  });
}
function _temp2(event) {
  if (!event || event.type !== "updated") {
    return;
  }
  const error = event.query?.state?.error;
  if (!error) {
    return;
  }
  if (error instanceof TRPCClientError) {
    const code = error.shape?.data?.code ?? error.data?.code;
    const path = String(event.query.queryKey?.[0] ?? "");
    if (code === "INTERNAL_SERVER_ERROR") {
      logError(error.message, "tRPC_INTERNAL_SERVER_ERROR", {
        path
      });
    }
    return;
  }
  logError(error instanceof Error ? error : new Error(String(error)), "REACT_QUERY_ERROR", {
    queryKey: JSON.stringify(event.query?.queryKey ?? null)
  });
}
function _temp3(event_0) {
  const mutation = event_0?.mutation;
  if (!mutation) {
    return;
  }
  const error_0 = mutation.state?.error;
  if (!error_0) {
    return;
  }
  if (error_0 instanceof TRPCClientError) {
    const code_0 = error_0.shape?.data?.code ?? error_0.data?.code;
    const path_0 = String(mutation.options?.mutationKey?.[0] ?? "");
    if (code_0 === "INTERNAL_SERVER_ERROR") {
      logError(error_0.message, "TRPC_MUTATION_INTERNAL_SERVER_ERROR", {
        path: path_0
      });
    }
    return;
  }
  logError(error_0 instanceof Error ? error_0 : new Error(String(error_0)), "REACT_QUERY_MUTATION_ERROR", {
    mutationKey: JSON.stringify(mutation.options?.mutationKey ?? null)
  });
}
function _temp4() {
  return isClientConsoleLoggingEnabled();
}
function _temp5() {
  return api.createClient({
    links: [loggerLink({
      enabled: _temp4
    }), authRedirectLink, httpBatchLink({
      url: `${getMiddlewareUrl({
        useRelativePathOnClient: true
      })}/api/trpc`
    })],
    transformer: superjson
  });
}